import React, { ReactNode, useEffect } from 'react';
import { Form, Button, Table } from 'react-bootstrap';
import { Link, useForm, usePage } from '@inertiajs/react';

import AuthedLayout from '../../Layouts/Authed';
import ProducerBookOfBusiness from '../../Data/ProducerBookOfBusiness';
import Valuation from '../../Data/Valuation';
import ProducerBobRow from './ProducerBooksOfBusiness/ProducerBobRow';
import { getEventValue, replaceNullWithEmpty } from './Helpers/RepeatingForms';
import FormHeader from '../../Components/Forms/FormHeader';
import InstructionsPanel from '../../Components/Forms/InstructionsPanel';
import AutosaveStatus from '../../Components/Forms/AutosaveStatus';
import useValuationStepAutosave from './Helpers/useValuationStepAutosave';
import MarkComplete from '../../Components/Forms/MarkComplete';

interface Props {
	valuation: Valuation;
	producer_bobs: ProducerBookOfBusiness[];
}

interface FormData {
	producer_bobs: Partial<ProducerBookOfBusiness>[];
	mark_complete: boolean;
}

const ProducerBooksOfBusiness = (props: Props) => {
	const { errors, readOnly } = usePage<any>().props;

	const { data, setData, post } = useForm<FormData>({
		producer_bobs: replaceNullWithEmpty(props.producer_bobs) ?? [],
		mark_complete: props.valuation.completed_steps?.includes('producer-bobs') ?? false,
	});
	const autosaveStatus = useValuationStepAutosave('producer-bobs', props.valuation.id, data);

	useEffect(() => {
		if (data.producer_bobs.length == 0) {
			addRow();
		}
	}, [props.producer_bobs]);

	const submit = (event: any) => {
		event.preventDefault();
		post(`/valuation-form/${props.valuation.id}/producer-bobs`);
	};

	const addRow = () => {
		setData({
			...data,
			producer_bobs: [
				...data.producer_bobs,
				{
					id: '',
					name: '',
					employee_benefits_total_book: '',
					p_c_total_book: '',
					producer_age: '',
					compensation_structure_splits: '',
				},
			] as ProducerBookOfBusiness[],
		});
	};

	const updateIndex = (event: React.ChangeEvent<any>, index: number) => {
		const newData: any = { ...data };
		newData.producer_bobs[index][event.target.name] = getEventValue(event);
		setData(newData);
	};

	const deleteIndex = (index: number) => {
		const newData: any = { ...data };
		newData.producer_bobs.splice(index, 1);
		setData(newData);
	};

	useEffect(() => {
		scrollTo(0,0);
	}, []);

	return (
		<div>
			<FormHeader
				agencyName={props.valuation.agency_name}
				effectiveDate={props.valuation.effective_date}
				pageName='Producer Books of Business'
			/>
			<InstructionsPanel>
				Use this tab to record the amount of agency commission and fees coded to the house or individual sales personnel. Please also detail how the producers are compensated, if they are commissioned please list the new and renewal commission rates.
			</InstructionsPanel>
			<Form onSubmit={submit} className='valuationForm'>
				<fieldset disabled={readOnly}>
				<Table striped borderless hover responsive>
					<thead>
						<tr>
							<th
								id='producer_bobNum'
								className='cell-xs sticky-cell'
							>
								#
							</th>
							<th id='name'>Producer Name</th>
							<th id='employee_benefits_total_book'>
								Employee Benefits Total Book $
							</th>
							<th id='p_c_total_book'>P&C Total Book $</th>
							<th id='producer_age'>Producer Age</th>
							<th id='compensation_structure_splits'>
								Compensation Structure Splits
							</th>
							<th id='delete_element'></th>
						</tr>
					</thead>
					<tbody>
						{data.producer_bobs.map(
							(
								producer_bob: Partial<ProducerBookOfBusiness>,
								index: number
							) => (
								<ProducerBobRow
									key={`producer_bob-${index}`}
									producer_bob={producer_bob}
									index={index}
									errors={errors}
									updateIndex={updateIndex}
									deleteIndex={deleteIndex}
								/>
							)
						)}
					</tbody>
				</Table>
				</fieldset>

				<div className='d-flex justify-content-between align-items-center'>
					<Button
						as={Link as any}
						href={`/valuation-form/${props.valuation.id}/lh-carriers`}
						variant='outline-primary'
						className='mt-4'
					>
						Back
					</Button>
					<div className='d-flex align-items-center'>
						{!readOnly && <AutosaveStatus status={autosaveStatus} />}
						{!readOnly && (
							<MarkComplete
								checked={data.mark_complete}
								onChange={(checked) => setData('mark_complete', checked)}
								className='mt-4 ms-4'
							/>
						)}
						<Button onClick={addRow} className='mt-4 ms-4'>
							Add Row
						</Button>
						{readOnly ? (
							<Button
								as={Link as any}
								href={`/valuation-form/${props.valuation.id}/non-recurring-revenues`}
								className='mt-4 ms-4'
							>
								Next
							</Button>
						) : (
							<Button type='submit' className='mt-4 ms-4'>
								Next
							</Button>
						)}
					</div>
				</div>
			</Form>
		</div>
	);
};

ProducerBooksOfBusiness.layout = (page: ReactNode) => (
	<AuthedLayout>{page}</AuthedLayout>
);

export default ProducerBooksOfBusiness;
